import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calculator, RotateCcw, Settings2, Sheet } from "lucide-react";
import { toast } from "sonner";
import type {
  ComponentPlacement,
  CutPiece,
  RunFitting,
  SupportPlacement,
  TerminationPlacement,
} from "@/hooks/use-pipe-sketch";
import { useEstimateSettings } from "@/hooks/use-estimate-settings";
import {
  buildEstimate,
  ESTIMATE_SECTIONS,
  money,
  type EstimateSection,
  type EstimateSettings,
} from "@/lib/pipe-estimating";
import { getMaterial, type MaterialId } from "@/lib/pipe-spec";

const RATE_FIELDS: Array<{
  key: keyof EstimateSettings;
  label: string;
  suffix: string;
  step?: number;
}> = [
  { key: "laborRate", label: "Crew rate", suffix: "$/hr", step: 1 },
  { key: "laborFactor", label: "Difficulty factor", suffix: "×", step: 0.05 },
  { key: "crewSize", label: "Crew size", suffix: "men", step: 1 },
  { key: "hoursPerDay", label: "Shift length", suffix: "hr", step: 0.5 },
  { key: "pipeWastePct", label: "Pipe waste", suffix: "%", step: 1 },
  { key: "priceIndexPct", label: "Price index", suffix: "%", step: 1 },
  { key: "consumablesPct", label: "Consumables", suffix: "% labor", step: 1 },
  { key: "materialMarkupPct", label: "Material markup", suffix: "%", step: 1 },
  { key: "taxPct", label: "Sales tax", suffix: "%", step: 0.25 },
  { key: "overheadPct", label: "Overhead", suffix: "%", step: 1 },
  { key: "profitPct", label: "Profit", suffix: "%", step: 1 },
];

export default function EstimatePanel({
  material,
  name,
  cutList,
  fittings,
  placements,
  supports,
  terminations,
}: {
  material: MaterialId;
  name?: string;
  cutList: CutPiece[];
  fittings: RunFitting[];
  placements: ComponentPlacement[];
  supports: SupportPlacement[];
  terminations: TerminationPlacement[];
}) {
  const {
    settings,
    setSetting,
    resetSettings,
    overrides,
    setOverride,
    clearOverride,
    clearAllOverrides,
    overrideCount,
  } = useEstimateSettings();
  const [showRates, setShowRates] = useState(false);

  const mat = getMaterial(material);

  const { lines, totals } = useMemo(
    () =>
      buildEstimate({
        material,
        cutList,
        fittings,
        placements,
        supports,
        terminations,
        settings,
        overrides,
      }),
    [material, cutList, fittings, placements, supports, terminations, settings, overrides],
  );

  const grouped = useMemo(() => {
    const map = new Map<EstimateSection, typeof lines>();
    for (const l of lines) {
      const arr = map.get(l.section) ?? [];
      arr.push(l);
      map.set(l.section, arr);
    }
    return ESTIMATE_SECTIONS.filter((s) => map.has(s)).map(
      (s) => [s, map.get(s)!] as const,
    );
  }, [lines]);

  const exportCsv = () => {
    const esc = (v: string | number) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const row = (...cells: (string | number)[]) => cells.map(esc).join(",");
    const rows = [
      row("Estimate", name ?? "Pipe sketch"),
      row("Material", `${mat.label} · ${mat.spec} · ${mat.joint}`),
      row("Crew rate ($/hr)", settings.laborRate),
      row("Difficulty factor", settings.laborFactor),
      "",
      row("Section", "Description", "Size", "Qty", "Unit", "Unit hrs", "Hours", "Unit cost", "Material", "Labor", "Total"),
      ...lines.map((l) =>
        row(
          l.section,
          l.description,
          l.size,
          l.qty.toFixed(2),
          l.unit,
          l.unitHours.toFixed(3),
          l.hours.toFixed(2),
          l.unitCost.toFixed(2),
          l.material.toFixed(2),
          l.labor.toFixed(2),
          l.total.toFixed(2),
        ),
      ),
      "",
      row("Total labor hours", totals.hours.toFixed(2)),
      row("Crew days", totals.crewDays.toFixed(2)),
      row("Labor cost", totals.laborCost.toFixed(2)),
      row("Consumables", totals.consumables.toFixed(2)),
      row("Material", totals.materialBase.toFixed(2)),
      row("Material markup", totals.markup.toFixed(2)),
      row("Sales tax", totals.tax.toFixed(2)),
      row("Subtotal", totals.subtotal.toFixed(2)),
      row("Overhead", totals.overhead.toFixed(2)),
      row("Profit", totals.profit.toFixed(2)),
      row("TOTAL", totals.grandTotal.toFixed(2)),
      row("Cost per foot", totals.costPerFoot.toFixed(2)),
    ];
    const blob = new Blob([`\uFEFF${rows.join("\r\n")}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(name ?? "pipe-sketch").replace(/[^\w-]+/g, "-").toLowerCase()}-estimate.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Estimate exported");
  };

  if (!cutList.length) {
    return (
      <div className="space-y-2 p-3">
        <h2 className="text-sm font-semibold">Estimate</h2>
        <p className="text-xs text-muted-foreground">
          Sketch a run and the estimate builds itself — labor hours and material dollars for every
          foot of pipe, weld, fitting, valve and hanger on the ISO.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Estimate</h2>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant={showRates ? "default" : "outline"}
            onClick={() => setShowRates((v) => !v)}
            aria-pressed={showRates}
            title="Rates and markups"
          >
            <Settings2 className="mr-1 h-3.5 w-3.5" /> Rates
          </Button>
          <Button size="sm" variant="outline" onClick={exportCsv} title="Export estimate as CSV">
            <Sheet className="mr-1 h-3.5 w-3.5" /> CSV
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-primary/40 bg-primary/5 p-2">
        <div className="flex items-baseline justify-between">
          <span className="text-xs text-muted-foreground">Sell price</span>
          <span className="font-mono text-lg font-semibold text-primary">
            {money(totals.grandTotal)}
          </span>
        </div>
        <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
          <span>{totals.hours.toFixed(1)} labor hr</span>
          <span>{totals.crewDays.toFixed(1)} crew days</span>
          <span>{money(totals.costPerFoot)}/ft</span>
        </div>
      </div>

      {showRates && (
        <div className="space-y-2 rounded-md border border-border p-2">
          <div className="grid grid-cols-2 gap-2">
            {RATE_FIELDS.map((f) => (
              <div key={f.key} className="space-y-0.5">
                <label
                  htmlFor={`rate-${f.key}`}
                  className="block text-[10px] uppercase tracking-wide text-muted-foreground"
                >
                  {f.label} <span className="normal-case">({f.suffix})</span>
                </label>
                <Input
                  id={`rate-${f.key}`}
                  type="number"
                  inputMode="decimal"
                  step={f.step}
                  value={settings[f.key]}
                  onChange={(e) => setSetting(f.key, Number(e.target.value) || 0)}
                  className="h-7 font-mono text-xs"
                />
              </div>
            ))}
          </div>
          <Button size="sm" variant="ghost" className="h-7 w-full" onClick={resetSettings}>
            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset rates to defaults
          </Button>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">
        <Calculator className="mr-1 inline h-3 w-3" />
        Labor units are typical field units for {mat.joint.toLowerCase()} work; prices are seed
        values. Tap any hour or dollar figure below to put your own number in — it sticks on this
        device.
      </p>

      {grouped.map(([section, items]) => {
        const secHours = items.reduce((s, l) => s + l.hours, 0);
        const secTotal = items.reduce((s, l) => s + l.total, 0);
        return (
          <div key={section} className="space-y-1">
            <div className="flex items-baseline justify-between border-b border-border pb-0.5">
              <h3 className="text-xs font-semibold">{section}</h3>
              <span className="font-mono text-[10px] text-muted-foreground">
                {secHours.toFixed(1)} hr · {money(secTotal)}
              </span>
            </div>
            {items.map((l) => (
              <div key={l.key} className="rounded-md border border-border px-2 py-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="flex-1 truncate text-xs">{l.description}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {l.qty.toFixed(l.unit === "ft" ? 1 : 0)} {l.unit}
                  </span>
                  <span className="font-mono text-xs font-medium">{money(l.total)}</span>
                </div>
                <div className="mt-1 flex items-center gap-1">
                  <label className="sr-only" htmlFor={`h-${l.key}`}>
                    {l.description} hours per {l.unit}
                  </label>
                  <Input
                    id={`h-${l.key}`}
                    type="number"
                    inputMode="decimal"
                    step={0.01}
                    value={Number(l.unitHours.toFixed(3))}
                    onChange={(e) => setOverride(l.key, { unitHours: Number(e.target.value) || 0 })}
                    className="h-6 flex-1 px-1 font-mono text-[10px]"
                    title={`Hours per ${l.unit} (default ${l.baseHours.toFixed(3)})`}
                  />
                  <span className="text-[9px] text-muted-foreground">hr/{l.unit}</span>
                  <label className="sr-only" htmlFor={`c-${l.key}`}>
                    {l.description} cost per {l.unit}
                  </label>
                  <Input
                    id={`c-${l.key}`}
                    type="number"
                    inputMode="decimal"
                    step={0.5}
                    value={Number(l.unitCost.toFixed(2))}
                    onChange={(e) => setOverride(l.key, { unitCost: Number(e.target.value) || 0 })}
                    className="h-6 flex-1 px-1 font-mono text-[10px]"
                    title={`Material $ per ${l.unit} (default ${l.baseCost.toFixed(2)})`}
                  />
                  <span className="text-[9px] text-muted-foreground">$/{l.unit}</span>
                  {l.edited && (
                    <button
                      type="button"
                      onClick={() => clearOverride(l.key)}
                      className="text-[9px] text-primary underline"
                      title="Reset this line to the default unit"
                    >
                      reset
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {overrideCount > 0 && (
        <Button size="sm" variant="ghost" className="h-7 w-full" onClick={clearAllOverrides}>
          <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset {overrideCount} edited unit
          {overrideCount === 1 ? "" : "s"}
        </Button>
      )}

      <div className="space-y-0.5 border-t border-border pt-2 text-xs">
        {[
          ["Labor", totals.laborCost],
          ["Consumables", totals.consumables],
          ["Material", totals.materialBase],
          ["Markup", totals.markup],
          ...(totals.tax ? ([["Sales tax", totals.tax]] as [string, number][]) : []),
          ["Subtotal", totals.subtotal],
          ["Overhead", totals.overhead],
          ["Profit", totals.profit],
        ].map(([label, value]) => (
          <div key={label as string} className="flex justify-between text-muted-foreground">
            <span>{label as string}</span>
            <span className="font-mono">{money(value as number)}</span>
          </div>
        ))}
        <div className="flex justify-between border-t border-border pt-1 text-sm font-semibold">
          <span>Total</span>
          <span className="font-mono text-primary">{money(totals.grandTotal)}</span>
        </div>
      </div>
    </div>
  );
}
